import {
  Add as AddIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  MenuBook as GuidesIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { titleText } from '../components/guide-editor/model';
import NewGuideDialog from '../components/guide-editor/NewGuideDialog';
import { useAuth } from '../contexts/GoogleAuthContext';
import { api, GuideDetail } from '../services/api-client';

const PAGE_SIZE = 20;

interface GuideRow extends GuideDetail {
  _loadError?: string;
}

export default function GuidesDashboard() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [newGuideOpen, setNewGuideOpen] = useState(false);

  const [slugs, setSlugs] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, GuideRow>>({});
  const [loadingSlugs, setLoadingSlugs] = useState(true);
  const [loadingPage, setLoadingPage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [publishedOnly, setPublishedOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const fetchSlugs = async () => {
    setLoadingSlugs(true);
    setError(null);
    try {
      const result = await api.getGuideSlugs({ publishedOnly });
      setSlugs(result);
      setPage(0);
    } catch (err: any) {
      setError(err.message || 'Failed to load guides');
    } finally {
      setLoadingSlugs(false);
    }
  };

  useEffect(() => {
    fetchSlugs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishedOnly]);

  const filteredSlugs = search.trim()
    ? slugs.filter((slug) => slug.toLowerCase().includes(search.trim().toLowerCase()))
    : slugs;

  const pageSlugs = filteredSlugs.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  useEffect(() => {
    const toLoad = pageSlugs.filter((slug) => !rows[slug]);
    if (toLoad.length === 0) return;

    let cancelled = false;
    setLoadingPage(true);
    Promise.all(
      toLoad.map(async (slug) => {
        try {
          const detail = await api.getGuide(slug);
          return { slug, detail };
        } catch (err: any) {
          return { slug, detail: { slug, _loadError: err.message || 'Failed to load' } as GuideRow };
        }
      })
    ).then((results) => {
      if (cancelled) return;
      setRows((prev) => {
        const next = { ...prev };
        for (const { slug, detail } of results) {
          next[slug] = detail as GuideRow;
        }
        return next;
      });
      setLoadingPage(false);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filteredSlugs.length, publishedOnly]);

  const totalPages = Math.max(1, Math.ceil(filteredSlugs.length / PAGE_SIZE));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GuidesIcon color="primary" />
          <Typography variant="h5" fontWeight={600}>
            Guides
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Postgres-stored destination guides from the Curated-By-Blog pipeline
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {hasPermission('content', 'edit') && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewGuideOpen(true)}>
              New guide
            </Button>
          )}
          <IconButton onClick={fetchSlugs} disabled={loadingSlugs}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>
      <NewGuideDialog open={newGuideOpen} onClose={() => setNewGuideOpen(false)} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search by slug..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
          sx={{ minWidth: 250 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={publishedOnly}
              onChange={(e) => setPublishedOnly(e.target.checked)}
            />
          }
          label="Published only"
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {filteredSlugs.length} guides
        </Typography>
      </Paper>

      {loadingSlugs ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filteredSlugs.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <GuidesIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No guides found</Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Guide</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Deterministic QA</TableCell>
                  <TableCell>Judge</TableCell>
                  <TableCell>Review</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageSlugs.map((slug) => {
                  const row = rows[slug];
                  return (
                    <TableRow
                      key={slug}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/guides/${slug}`)}
                    >
                      <TableCell>
                        {row ? (
                          <>
                            <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 350 }}>
                              {titleText(row.title) || row.name || slug}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {slug}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {slug}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {!row ? (
                          <CircularProgress size={16} />
                        ) : row._loadError ? (
                          <Tooltip title={row._loadError}>
                            <Chip label="Load failed" color="error" size="small" />
                          </Tooltip>
                        ) : (
                          <Chip
                            label={row.draft ? 'Draft' : row.status || 'Published'}
                            color={row.draft ? 'default' : 'success'}
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {row?.qa?.report ? (
                          <Tooltip
                            title={
                              row.qa.report.failures?.length
                                ? row.qa.report.failures.join(', ')
                                : 'No failures'
                            }
                          >
                            <Chip
                              label={
                                row.qa.report.ok
                                  ? 'Pass'
                                  : `Fail (${row.qa.report.failures?.length ?? 0})`
                              }
                              color={row.qa.report.ok ? 'success' : 'error'}
                              size="small"
                            />
                          </Tooltip>
                        ) : row && !row._loadError ? (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {row?.qa?.judge?.one_line ? (
                          <Tooltip title={row.qa.judge.one_line}>
                            <Typography
                              variant="caption"
                              noWrap
                              sx={{ display: 'block', maxWidth: 260 }}
                            >
                              {row.qa.judge.verdict ? `[${row.qa.judge.verdict}] ` : ''}
                              {row.qa.judge.one_line}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Not judged yet
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {row?.review ? (
                          <Chip
                            label={row.review.decision}
                            color={
                              row.review.decision === 'approve'
                                ? 'success'
                                : row.review.decision === 'reject'
                                ? 'error'
                                : 'warning'
                            }
                            size="small"
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Unreviewed
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2 }}>
            <IconButton disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
              <PrevIcon />
            </IconButton>
            <Typography variant="body2" color="text.secondary">
              Page {page + 1} of {totalPages}
              {loadingPage && ' · loading…'}
            </Typography>
            <IconButton
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            >
              <NextIcon />
            </IconButton>
          </Box>
        </>
      )}
    </Box>
  );
}
